import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Global load-balancing and geo-routing console (part 79).
 *
 * The page shows the region catalogue, the tenant's sticky routes and the append-only decision
 * log, and lets an operator resolve a country with a simulated outage before changing a route.
 * The decision is made by the API (domain/geo); this page never re-implements the ranking, it only
 * renders the preferred and selected regions and the explanation the API returned.
 *
 * Like the API mount, the page is not wired from App.tsx in this part: both are one-line changes
 * reviewed with the first operator who needs the console.
 */

interface GeoRegion {
  code: string
  name_th: string
  zone: string
  jurisdiction: string
  base_latency_ms: number
  capacity_weight: number
  status: 'active' | 'draining' | 'offline'
  notes: string
  default: boolean
  updated_at: string
}

interface GeoRoute {
  id: string
  tenant_id: string
  country_code: string
  primary_region: string
  fallback_region?: string
  sticky: boolean
  status: 'active' | 'paused'
  notes: string
  updated_by?: string
  created_at: string
  updated_at: string
}

interface GeoCandidate {
  region: string
  name_th: string
  state: 'unknown' | 'healthy' | 'degraded' | 'unhealthy'
  eligible: boolean
  latency_ms: number
  weight: number
  score: number
  detail_th: string
}

interface GeoDecision {
  country: string
  country_known: boolean
  source: 'cf-ipcountry' | 'x-country' | 'query' | 'none'
  tenant_id?: string
  basis: 'latency' | 'tenant_route' | 'default'
  sticky: boolean
  failover: boolean
  preferred_region: string
  selected_region: string
  selected_name_th: string
  reason_th: string
  explain: string[]
  candidates: GeoCandidate[]
  decided_at: string
}

interface GeoDecisionRecord {
  id: string
  country_code: string
  country_known: boolean
  source: string
  basis: 'latency' | 'tenant_route' | 'default'
  sticky: boolean
  failover: boolean
  preferred_region: string
  selected_region: string
  reason_th: string
  decided_at: string
}

const REGIONS_KEY = ['geo-regions']
const ROUTES_KEY = ['geo-routes']
const DECISIONS_KEY = ['geo-decisions']

const BASIS_LABEL: Record<string, string> = {
  latency: 'ความหน่วงต่ำสุด',
  tenant_route: 'เส้นทางขององค์กร',
  default: 'ภูมิภาคเริ่มต้น',
}

const SOURCE_LABEL: Record<string, string> = {
  'cf-ipcountry': 'CF-IPCountry',
  'x-country': 'X-Country',
  query: 'พารามิเตอร์ทดสอบ',
  none: 'ไม่มี header',
}

const STATE_LABEL: Record<string, string> = {
  healthy: 'พร้อมให้บริการ',
  degraded: 'ทำงานแบบจำกัด',
  unhealthy: 'ไม่พร้อมให้บริการ',
  unknown: 'ไม่มีการวัดล่าสุด',
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

export function Geo() {
  const qc = useQueryClient()
  const [country, setCountry] = useState('TH')
  const [offline, setOffline] = useState('')
  const [submitted, setSubmitted] = useState({ country: 'TH', offline: '' })
  const [routeCountry, setRouteCountry] = useState('TH')
  const [primary, setPrimary] = useState('')
  const [fallback, setFallback] = useState('')
  const [sticky, setSticky] = useState(true)
  const [notes, setNotes] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const regions = useQuery({
    queryKey: REGIONS_KEY,
    queryFn: () => list<GeoRegion>('/v1/geo/regions'),
    staleTime: 60_000,
  })

  const routes = useQuery({
    queryKey: ROUTES_KEY,
    queryFn: () => list<GeoRoute>('/v1/geo/routes'),
    staleTime: 15_000,
  })

  const decisions = useQuery({
    queryKey: DECISIONS_KEY,
    queryFn: () => list<GeoDecisionRecord>('/v1/geo/decisions?limit=50'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })

  const preview = useQuery({
    queryKey: ['geo-resolve', submitted.country, submitted.offline],
    queryFn: () => {
      const params = new URLSearchParams()
      if (submitted.country.trim()) params.set('country', submitted.country.trim())
      if (submitted.offline.trim()) params.set('offline', submitted.offline.trim())
      const suffix = params.toString()
      return api<GeoDecision>(`/v1/geo/resolve${suffix ? `?${suffix}` : ''}`)
    },
    staleTime: 5_000,
  })

  const regionRows = regions.data ?? []
  const routeRows = routes.data ?? []
  const decisionRows = decisions.data ?? []
  const defaultRegion = useMemo(
    () => regionRows.find((region) => region.default) ?? regionRows[0],
    [regionRows],
  )

  useEffect(() => {
    if (!primary && regionRows.length > 0) {
      setPrimary(defaultRegion?.code ?? regionRows[0].code)
    }
  }, [defaultRegion, primary, regionRows])

  const saveRoute = useMutation({
    mutationFn: (body: {
      country_code: string
      primary_region: string
      fallback_region: string
      sticky: boolean
      notes: string
    }) => apiWrite<GeoRoute>('/v1/geo/routes', 'POST', body),
    onSuccess: (row) => {
      setNotice(`บันทึกเส้นทางของ ${row.country_code} ไปยัง ${row.primary_region} แล้ว`)
      setFormError(null)
      qc.invalidateQueries({ queryKey: ROUTES_KEY })
      qc.invalidateQueries({ queryKey: ['geo-resolve'] })
    },
    onError: (error) => {
      setNotice(null)
      setFormError(errorText(error, 'บันทึกเส้นทางไม่สำเร็จ'))
    },
  })

  const normalizedCountry = routeCountry.trim().toUpperCase()
  const countryValid = normalizedCountry === '*' || /^[A-Z]{2}$/.test(normalizedCountry)
  const canSave = countryValid && primary !== '' && fallback !== primary && !saveRoute.isPending

  function submitResolve() {
    setSubmitted({ country, offline })
  }

  function commitRoute() {
    if (!canSave) return
    setNotice(null)
    setFormError(null)
    saveRoute.mutate({
      country_code: normalizedCountry,
      primary_region: primary,
      fallback_region: fallback,
      sticky,
      notes: notes.trim(),
    })
  }

  const activeRegions = regionRows.filter((region) => region.status === 'active').length
  const failovers = decisionRows.filter((row) => row.failover).length

  return (
    <>
      <PageHead
        title="กำหนดเส้นทางตามภูมิภาค"
        sub="เลือกภูมิภาคที่ให้บริการตามรหัสประเทศจาก Cloudflare · คะแนนความหน่วงถ่วงด้วยน้ำหนักความจุ · ภูมิภาคที่ไม่พร้อมจะถูกข้ามไปยังภูมิภาคถัดไปโดยอัตโนมัติ"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{regionRows.length} ภูมิภาค</span>
        <span className="chip">{activeRegions} เปิดให้บริการ</span>
        <span className="chip">ค่าเริ่มต้น: {defaultRegion?.name_th ?? '-'}</span>
        <span className="chip">{routeRows.filter((row) => row.status === 'active').length} เส้นทางที่ใช้งาน</span>
        {failovers > 0 ? <span className="chip gold">{failovers} ครั้งที่ต้องสำรองภูมิภาค</span> : null}
      </div>

      <div className="grid-4 stagger">
        <Stat
          label="ภูมิภาคเริ่มต้น"
          value={defaultRegion?.code ?? '-'}
          foot={defaultRegion ? `${defaultRegion.name_th} · ใช้เมื่อไม่รู้จักรหัสประเทศ` : 'ยังอ่านแคตตาล็อกไม่ได้'}
        />
        <Stat
          label="ภูมิภาคทั้งหมด"
          value={regionRows.length}
          unit="แห่ง"
          foot="รายการจาก platform.regions ซึ่งเป็นข้อมูลอ้างอิงกลาง"
        />
        <Stat
          label="เส้นทางขององค์กร"
          value={routeRows.length}
          unit="รายการ"
          foot="กำหนดภูมิภาคหลักและภูมิภาคสำรองต่อประเทศ"
        />
        <Stat
          label="บันทึกการตัดสินใจ"
          value={decisionRows.length}
          unit="รายการ"
          foot="บันทึกล่าสุด 50 รายการ · เพิ่มอย่างเดียว แก้ไขไม่ได้"
        />
      </div>

      <div className="grid-main" style={{ marginTop: 18 }}>
        <div className="card card-pad">
          <div className="card-label">ทดสอบเส้นทาง</div>
          <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 8 }}>
            ใช้พารามิเตอร์ country เพื่อทดสอบโดยไม่ต้องมี header จาก Cloudflare · พารามิเตอร์ offline
            จำลองภูมิภาคที่ล่มและไม่ถูกบันทึกลงฐานข้อมูล
          </p>

          <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ marginTop: 0, width: 130 }}>
              <label htmlFor="geo-country">รหัสประเทศ</label>
              <input
                id="geo-country"
                className="input mono"
                value={country}
                maxLength={8}
                onChange={(event) => setCountry(event.target.value.toUpperCase())}
                placeholder="TH"
              />
            </div>
            <div className="field" style={{ marginTop: 0, flex: 1, minWidth: 220 }}>
              <label htmlFor="geo-offline">ภูมิภาคที่จำลองว่าล่ม</label>
              <input
                id="geo-offline"
                className="input mono"
                value={offline}
                onChange={(event) => setOffline(event.target.value)}
                placeholder="ap-southeast-7, ap-southeast-1"
              />
            </div>
            <button className="btn btn-primary" onClick={submitResolve} disabled={preview.isFetching}>
              {preview.isFetching ? 'กำลังตรวจ…' : 'ตรวจเส้นทาง'}
            </button>
          </div>

          {preview.isLoading ? (
            <div style={{ marginTop: 18 }}><Skel h={140} /></div>
          ) : preview.isError ? (
            <div className="form-error" style={{ marginTop: 16 }}>
              {errorText(preview.error, 'ตรวจเส้นทางไม่สำเร็จ')}
            </div>
          ) : preview.data ? (
            <div style={{ marginTop: 18 }}>
              <div className="rule" />
              <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 220 }}>
                  <div className="dim" style={{ fontSize: 11 }}>ภูมิภาคที่เลือก</div>
                  <div style={{ fontSize: 26, marginTop: 4 }}>{preview.data.selected_name_th}</div>
                  <div className="mono dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                    {preview.data.selected_region}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <span className="chip">{BASIS_LABEL[preview.data.basis] ?? preview.data.basis}</span>
                  {preview.data.failover ? <span className="chip gold" style={{ marginLeft: 6 }}>สำรองภูมิภาค</span> : null}
                  {preview.data.sticky ? <span className="chip gold" style={{ marginLeft: 6 }}>sticky</span> : null}
                  <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                    ประเทศ {preview.data.country || 'ไม่ระบุ'} · ที่มา {SOURCE_LABEL[preview.data.source] ?? preview.data.source}
                  </div>
                </div>
              </div>

              <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '14px 0 0', paddingLeft: 18 }}>
                {preview.data.explain.map((line) => <li key={line}>{line}</li>)}
              </ul>

              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>ภูมิภาค</th>
                      <th style={{ width: 110 }} className="num">ความหน่วง</th>
                      <th style={{ width: 80 }} className="num">น้ำหนัก</th>
                      <th style={{ width: 90 }} className="num">คะแนน</th>
                      <th style={{ width: 130 }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.candidates.map((candidate) => {
                      const selected = candidate.region === preview.data?.selected_region
                      return (
                        <tr key={candidate.region} style={{ cursor: 'default' }}>
                          <td>
                            <div style={{ color: selected ? 'var(--color-gold)' : 'var(--color-ivory)' }}>
                              {candidate.name_th}
                            </div>
                            <div className="mono dim" style={{ fontSize: 10.5, marginTop: 2 }}>{candidate.region}</div>
                          </td>
                          <td className="num tnum">{candidate.latency_ms} ms</td>
                          <td className="num tnum">{candidate.weight}/100</td>
                          <td className="num tnum">{candidate.score.toFixed(3)}</td>
                          <td>
                            <span
                              className={`chip${selected ? ' gold' : ''}`}
                              style={candidate.eligible ? undefined : { color: 'var(--color-crimson)', borderColor: 'currentColor' }}
                              title={candidate.detail_th}
                            >
                              {selected ? 'เลือกแล้ว' : candidate.eligible ? STATE_LABEL[candidate.state] ?? 'พร้อม' : 'ไม่พร้อม'}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">กำหนดเส้นทางขององค์กร</div>
          <p className="dim" style={{ fontSize: 12, lineHeight: 1.8, marginTop: 8 }}>
            ภูมิภาคหลักจะถูกตรึงไว้เมื่อ sticky เปิด และจะเปลี่ยนไปภูมิภาคสำรองก็ต่อเมื่อภูมิภาคหลักไม่พร้อมใช้งาน
          </p>

          <div className="field">
            <label htmlFor="geo-route-country">ประเทศหรือ * สำหรับทุกประเทศ</label>
            <input
              id="geo-route-country"
              className="input mono"
              value={routeCountry}
              maxLength={2}
              onChange={(event) => setRouteCountry(event.target.value.toUpperCase())}
              placeholder="TH"
            />
            {!countryValid && routeCountry.length > 0 ? (
              <div className="dim" style={{ fontSize: 10.5, marginTop: 5, color: 'var(--color-crimson)' }}>
                ต้องเป็นรหัสประเทศสองตัวอักษร หรือ * เท่านั้น
              </div>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="geo-route-primary">ภูมิภาคหลัก</label>
            <select
              id="geo-route-primary"
              className="select"
              value={primary}
              onChange={(event) => setPrimary(event.target.value)}
            >
              <option value="">เลือกภูมิภาค</option>
              {regionRows.map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name_th} · {region.code}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="geo-route-fallback">ภูมิภาคสำรอง (ไม่บังคับ)</label>
            <select
              id="geo-route-fallback"
              className="select"
              value={fallback}
              onChange={(event) => setFallback(event.target.value)}
            >
              <option value="">ไม่ระบุ</option>
              {regionRows.filter((region) => region.code !== primary).map((region) => (
                <option key={region.code} value={region.code}>
                  {region.name_th} · {region.code}
                </option>
              ))}
            </select>
          </div>

          <label className="row" style={{ gap: 8, marginTop: 12, cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={sticky} onChange={(event) => setSticky(event.target.checked)} />
            ตรึงผู้เช่ารายนี้ไว้กับภูมิภาคหลัก (sticky)
          </label>

          <div className="field">
            <label htmlFor="geo-route-notes">หมายเหตุ</label>
            <input
              id="geo-route-notes"
              className="input"
              value={notes}
              maxLength={2000}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="เช่น ข้อกำหนดถิ่นที่อยู่ของข้อมูลของลูกค้ารายนี้"
            />
          </div>

          <button
            className="btn btn-primary"
            style={{ marginTop: 16, width: '100%' }}
            disabled={!canSave}
            onClick={commitRoute}
          >
            {saveRoute.isPending ? 'กำลังบันทึก…' : 'บันทึกเส้นทาง'}
          </button>
          {formError ? <div className="form-error" style={{ marginTop: 10 }}>{formError}</div> : null}
          {notice ? (
            <div className="dim" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.7 }}>{notice}</div>
          ) : null}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row">
          <span className="card-label">เส้นทางที่กำหนดไว้</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ประเทศที่ระบุจะมีความเฉพาะเจาะจงกว่า *
          </span>
        </div>
        {routes.isLoading ? (
          <div style={{ marginTop: 14 }}><Skel h={120} /></div>
        ) : routes.isError ? (
          <div className="form-error" style={{ marginTop: 14 }}>
            {errorText(routes.error, 'โหลดเส้นทางไม่สำเร็จ')}
          </div>
        ) : routeRows.length === 0 ? (
          <div style={{ marginTop: 12 }}>
            <Empty
              title="ยังไม่มีเส้นทางขององค์กร"
              body="เมื่อไม่มีเส้นทาง ระบบจะใช้ตารางประเทศและคะแนนความหน่วงจากแคตตาล็อกกลาง"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>ประเทศ</th>
                  <th>ภูมิภาคหลัก</th>
                  <th>ภูมิภาคสำรอง</th>
                  <th style={{ width: 90 }}>Sticky</th>
                  <th style={{ width: 90 }}>สถานะ</th>
                  <th>แก้ไขล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {routeRows.map((route) => (
                  <tr key={route.id} style={{ cursor: 'default' }}>
                    <td className="mono">{route.country_code}</td>
                    <td>
                      <div>{regionRows.find((region) => region.code === route.primary_region)?.name_th ?? route.primary_region}</div>
                      <div className="mono dim" style={{ fontSize: 10.5, marginTop: 2 }}>{route.primary_region}</div>
                    </td>
                    <td>
                      {route.fallback_region ? (
                        <>
                          <div>{regionRows.find((region) => region.code === route.fallback_region)?.name_th ?? route.fallback_region}</div>
                          <div className="mono dim" style={{ fontSize: 10.5, marginTop: 2 }}>{route.fallback_region}</div>
                        </>
                      ) : <span className="dim">ไม่ระบุ</span>}
                    </td>
                    <td>{route.sticky ? 'เปิด' : 'ปิด'}</td>
                    <td>
                      <span className={`chip${route.status === 'active' ? ' gold' : ''}`}>
                        {route.status === 'active' ? 'ใช้งาน' : 'ระงับ'}
                      </span>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(route.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="row">
          <span className="card-label">บันทึกการตัดสินใจ</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            เพิ่มอย่างเดียว · 50 รายการล่าสุด · รีเฟรชทุก 30 วินาที
          </span>
        </div>
        {decisions.isLoading ? (
          <div style={{ marginTop: 14 }}><Skel h={140} /></div>
        ) : decisions.isError ? (
          <div className="form-error" style={{ marginTop: 14 }}>
            {errorText(decisions.error, 'โหลดบันทึกการตัดสินใจไม่สำเร็จ')}
          </div>
        ) : decisionRows.length === 0 ? (
          <div style={{ marginTop: 12 }}>
            <Empty
              title="ยังไม่มีการบันทึกการตัดสินใจ"
              body="การเรียก POST /v1/geo/resolve จะบันทึกผลพร้อมเหตุผลและรายการภูมิภาคที่พิจารณาไว้ที่นี่"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>เวลา</th>
                  <th style={{ width: 80 }}>ประเทศ</th>
                  <th style={{ width: 160 }}>ที่มา</th>
                  <th style={{ width: 150 }}>ภูมิภาคที่เลือก</th>
                  <th style={{ width: 130 }}>เกณฑ์</th>
                  <th style={{ width: 110 }}>ผลลัพธ์</th>
                </tr>
              </thead>
              <tbody>
                {decisionRows.map((row) => (
                  <tr key={row.id} style={{ cursor: 'default' }}>
                    <td className="dim" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{thaiDateTime(row.decided_at)}</td>
                    <td className="mono">{row.country_code || '-'}</td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{SOURCE_LABEL[row.source] ?? row.source}</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>
                      {row.selected_region}
                      {row.failover ? <span className="chip gold" style={{ marginLeft: 6 }}>สำรอง</span> : null}
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{BASIS_LABEL[row.basis] ?? row.basis}</td>
                    <td>
                      <span className="chip">{row.country_known ? 'รู้จักประเทศ' : 'ใช้ค่าเริ่มต้น'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 14 }}>
        รหัสประเทศมาจาก header CF-IPCountry ของ Cloudflare โดยตรง · คำขอที่ไม่มี header จะใช้ภูมิภาคเริ่มต้น
        ไม่ใช่ภูมิภาคที่เร็วที่สุด · การบันทึกเส้นทางเป็นแบบ POST และต้องมี Idempotency-Key
      </div>
    </>
  )
}
